import type { MatchPhase } from "./state.js";

/** Where each period's regulation time ends on the match clock. */
const PERIOD_END: Partial<Record<MatchPhase, number>> = {
  first_half: 45,
  second_half: 90,
  extra_first: 105,
  extra_second: 120,
};
const ENDS = [45, 90, 105, 120];

/** The minute an event falls in: 0:48 → 1, 15:01 → 16, 15:00 → 15. */
const ordinal = (minute: number, second: number) => (second > 0 ? minute + 1 : minute);

/** Stoppage minute of an event recorded at this clock time, or undefined in
 *  regulation time: 46:30 in the first half is the 47th minute → 2 ("45+2").
 *  It comes from the clock passing the period's end — not from the "+N"
 *  board, which only says how long the stoppage will be. */
export function stoppageMinute(phase: MatchPhase, minute: number, second: number): number | undefined {
  const end = PERIOD_END[phase];
  if (end === undefined) return undefined;
  const n = ordinal(minute, second);
  return n > end ? n - end : undefined;
}

/** Stoppage minute of a clock time read against the period it runs past —
 *  for editing a logged stoppage event, where the phase is no longer known:
 *  47:21 → 3 (45+3), 92:00 → 2 (90+2); undefined at or before 45:00. */
export function clockStoppage(minute: number, second: number): number | undefined {
  const n = ordinal(minute, second);
  const end = [...ENDS].reverse().find((e) => e < n);
  return end === undefined ? undefined : n - end;
}

/** Shows an event's minute in the football convention.
 *  - Regulation: if any second of the minute has elapsed it rounds up
 *    (0:48 → "1", 15:01 → "16", 15:00 → "15").
 *  - Stoppage (`added` set): a base minute (45/90/105/120, as PFL and manual
 *    edits store it) shows "45+added"; a live clock time past a period's end
 *    shows its stoppage minute from the clock — 47:26 → "45+3" — so events
 *    that stored the "+N" board as `added` still read right. */
export function formatEventMinute(minute: number, second: number, added?: number | null): string {
  if (added && added > 0) {
    if (ENDS.includes(minute)) return `${minute}+${added}`;
    const end = [...ENDS].reverse().find((e) => e < minute);
    if (end !== undefined) return `${end}+${ordinal(minute, second) - end}`;
  }
  return `${ordinal(minute, second)}`;
}
